import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { campaignAdProductLabel } from '@/types/spCampaigns';
import type { CampaignBudgetBulkUpdate } from '@/api/campaignBudgetUpdate';
import type { SpCampaignNormalized } from '@/types/spCampaigns';

type BulkCampaignBudgetPreviewRow = {
  campaign: SpCampaignNormalized;
  newBudget: number;
  error: string | null;
};

type BulkCampaignBudgetConfirmModalProps = {
  rows: BulkCampaignBudgetPreviewRow[];
  pending: boolean;
  formatCurrency: (amount: number) => string;
  onConfirm: () => void;
  onCancel: () => void;
};

export function BulkCampaignBudgetConfirmModal({
  rows,
  pending,
  formatCurrency,
  onConfirm,
  onCancel,
}: BulkCampaignBudgetConfirmModalProps) {
  const hasErrors = rows.some((row) => row.error);
  const validUpdates: CampaignBudgetBulkUpdate[] = rows
    .filter((row) => !row.error)
    .map((row) => ({
      campaignId: row.campaign.campaignId,
      adProduct: row.campaign.adProduct,
      budget: row.newBudget,
      budgetType: row.campaign.budgetType || 'DAILY',
      previousBudget: row.campaign.budget,
    }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[80vh] w-full max-w-3xl flex-col rounded-xl bg-surface p-5 shadow-xl">
        <h3 className="text-base font-semibold text-foreground">Confirm budget updates</h3>
        <p className="mt-1 text-sm text-foreground-muted">
          Review the new daily budgets before applying changes to Amazon Ads.
        </p>

        <div className="mt-4 min-h-0 flex-1 overflow-auto rounded-xl border border-border">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-muted border-b border-border">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-foreground-muted">Campaign</th>
                <th className="px-3 py-2 text-left font-semibold text-foreground-muted">Ad product</th>
                <th className="px-3 py-2 text-right font-semibold text-foreground-muted">Current</th>
                <th className="px-3 py-2 text-right font-semibold text-foreground-muted">New</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((row) => (
                <tr key={`${row.campaign.adProduct}-${row.campaign.campaignId}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-foreground">{row.campaign.name}</div>
                    {row.error ? (
                      <div className="mt-0.5 text-xs text-error">{row.error}</div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-foreground-muted">
                    {campaignAdProductLabel(row.campaign.adProduct)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatCurrency(row.campaign.budget)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">
                    {row.error ? '—' : formatCurrency(row.newBudget)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-3 text-xs text-foreground-subtle">
          {validUpdates.length} campaign{validUpdates.length === 1 ? '' : 's'} ready to update.
        </p>

        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={pending || hasErrors || validUpdates.length === 0}
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply updates'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export type { BulkCampaignBudgetPreviewRow };
